import { z } from "zod";
import { FoundationActorRefSchema, FoundationAuditContextSchema, FoundationClinicalReferenceSchema, FoundationSemVerSchema, FoundationTimestampSchema, OwnerAssignmentSchema, PatientContextRefSchema, } from "./foundation.js";
export const ExpectedEventTypeSchema = z.enum([
    "delivery_receipt",
    "lab_result",
    "imaging_report",
    "referral_acceptance",
    "appointment_booking",
    "consult_report",
    "medication_monitoring",
    "patient_acknowledgement",
    "form_response",
    "public_health_response",
    "other",
]);
export const ExpectedEventStatusSchema = z.enum([
    "open",
    "waiting",
    "received",
    "matched",
    "assigned_for_review",
    "reviewed",
    "patient_contact_pending",
    "closure_pending",
    "closed",
    "overdue",
    "escalated",
    "cancelled",
    "cannot_complete",
]);
export const ExpectedEventClosureCriterionSchema = z
    .object({
    criterionId: z.string(),
    description: z.string(),
    required: z.boolean(),
    satisfied: z.boolean().optional(),
    satisfiedAt: FoundationTimestampSchema.optional(),
    evidenceRef: FoundationClinicalReferenceSchema.optional(),
    satisfiedBy: FoundationActorRefSchema.optional(),
})
    .strict();
export const ExpectedEventHistoryEntrySchema = z
    .object({
    at: FoundationTimestampSchema,
    fromStatus: z.string(),
    toStatus: z.string(),
    actor: FoundationActorRefSchema,
    reason: z.string(),
    eventId: z.string().optional(),
})
    .strict();
const ExpectedEventTimingPolicyFields = {
    timezone: z.string().optional(),
    businessCalendarId: z.string().optional(),
    snoozedUntil: FoundationTimestampSchema.optional(),
};
export const ExpectedEventDurationSchema = z
    .string()
    .regex(/^P(?=\d|T\d)(?:\d+D)?(?:T(?=\d)(?:\d+H)?(?:\d+M)?(?:\d+(?:\.\d+)?S)?)?$/);
/** Existing absolute-clock representation; clockState remains optional for v0.1 compatibility. */
export const RunningExpectedEventTimingSchema = z
    .object({
    clockState: z.literal("running").optional(),
    clockStartsAt: FoundationTimestampSchema,
    expectedBy: FoundationTimestampSchema,
    overdueAt: FoundationTimestampSchema,
    ...ExpectedEventTimingPolicyFields,
})
    .strict();
/** A dormant clock whose deadlines are calculated only after its upstream event closes. */
export const WaitingExpectedEventTimingSchema = z
    .object({
    clockState: z.literal("waiting_for_dependency"),
    clockStartsAfterEventId: z.string().min(1),
    expectedWithin: ExpectedEventDurationSchema,
    overdueWithin: ExpectedEventDurationSchema,
    ...ExpectedEventTimingPolicyFields,
})
    .strict();
export const ExpectedEventTimingSchema = z.union([
    RunningExpectedEventTimingSchema,
    WaitingExpectedEventTimingSchema,
]);
export const ExpectedEventSchema = z
    .object({
    schemaVersion: FoundationSemVerSchema,
    expectedEventId: z.string(),
    patientContext: PatientContextRefSchema,
    encounterId: z.string().optional(),
    sourceAction: z
        .object({
        careBundleId: z.string(),
        actionId: z.string(),
        transactionId: z.string(),
        externalIdentifier: z.string().optional(),
    })
        .strict(),
    eventType: ExpectedEventTypeSchema,
    status: ExpectedEventStatusSchema,
    createdAt: FoundationTimestampSchema,
    dependsOnEventIds: z.array(z.string().min(1)).optional(),
    timing: ExpectedEventTimingSchema,
    owners: OwnerAssignmentSchema,
    receiptMatching: z
        .object({
        strategy: z.enum(["exact_external_id", "multi_key", "manual", "none"]),
        correlationKeys: z.record(z.string(), z.string()),
        confidenceThreshold: z.number().min(0).max(1),
        allowAutoMatch: z.boolean().optional(),
    })
        .strict(),
    escalationPolicy: z
        .object({
        policyId: z.string(),
        policyVersion: z.string(),
        steps: z.array(z
            .object({
            after: z.string().regex(/^P/),
            action: z.enum([
                "notify_owner",
                "notify_backup",
                "assign_pool",
                "create_staff_exception",
                "contact_destination",
                "contact_patient",
                "escalate_medical_director",
                "other",
            ]),
            targetId: z.string().optional(),
            templateId: z.string().optional(),
        })
            .strict()),
        stopOnStatus: z.array(z.string()).optional(),
    })
        .strict(),
    communicationPolicy: z
        .object({
        patientCommunicationRequired: z.boolean(),
        sendOnlyAfterClinicianReview: z.boolean(),
        allowedChannels: z.array(z.string()).optional(),
        languagePreferenceSource: z.string().optional(),
        templateId: z.string().optional(),
        maximumAttempts: z.number().int().min(0).optional(),
        recordAcknowledgement: z.boolean().optional(),
    })
        .strict(),
    closureCriteria: z.array(ExpectedEventClosureCriterionSchema).min(1),
    receivedArtifactRefs: z.array(FoundationClinicalReferenceSchema).optional(),
    history: z.array(ExpectedEventHistoryEntrySchema),
    closedAt: FoundationTimestampSchema.optional(),
    closureReason: z.string().optional(),
    auditContext: FoundationAuditContextSchema.optional(),
})
    .strict()
    .superRefine((event, context) => {
    if (event.timing.clockState !== "waiting_for_dependency")
        return;
    if (event.status !== "waiting") {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A dependency-gated Expected Event must remain waiting until its clock starts",
        });
    }
    if (!event.dependsOnEventIds?.includes(event.timing.clockStartsAfterEventId)) {
        context.addIssue({
            code: "custom",
            path: ["dependsOnEventIds"],
            message: "Dependency-gated timing must reference a declared upstream Expected Event",
        });
    }
});
//# sourceMappingURL=expected-event.js.map